<template>
  <div :class="cardClasses">
    <div v-if="slots.header || title" class="ui-card__header">
      <slot name="header">
        <h3 v-if="title" class="ui-card__title">{{ title }}</h3>
      </slot>
    </div>

    <div class="ui-card__body">
      <slot></slot>
    </div>

    <div v-if="slots.footer" class="ui-card__footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, useSlots } from "vue";

const slots = useSlots();

interface Props {
  title?: string;
  shadow?: "none" | "sm" | "md" | "lg";
  padding?: "none" | "sm" | "md" | "lg";
  hoverable?: boolean;
  bordered?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  shadow: "sm",
  padding: "md",
  hoverable: false,
  bordered: true,
});

const cardClasses = computed(() => {
  const classes = ["ui-card"];
  classes.push(`ui-card--shadow-${props.shadow}`);
  classes.push(`ui-card--padding-${props.padding}`);
  if (props.hoverable) classes.push("ui-card--hoverable");
  if (props.bordered) classes.push("ui-card--bordered");
  return classes;
});
</script>

<style scoped>
.ui-card {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
}

.ui-card--bordered {
  border: 1px solid #e5e7eb;
}

/* Shadow variants */
.ui-card--shadow-none {
  box-shadow: none;
}

.ui-card--shadow-sm {
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.ui-card--shadow-md {
  box-shadow:
    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.ui-card--shadow-lg {
  box-shadow:
    0px 10px 15px -3px rgba(0, 0, 0, 0.1),
    0px 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.ui-card--hoverable:hover {
  transform: translateY(-2px);
  box-shadow:
    0px 10px 15px -3px rgba(0, 0, 0, 0.1),
    0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

/* Header */
.ui-card__header {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
}

.ui-card__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #151d48;
}

/* Body */
.ui-card__body {
  flex: 1;
}

.ui-card--padding-none .ui-card__body {
  padding: 0;
}

.ui-card--padding-sm .ui-card__body {
  padding: 12px;
}

.ui-card--padding-md .ui-card__body {
  padding: 20px;
}

.ui-card--padding-lg .ui-card__body {
  padding: 32px;
}

/* Footer */
.ui-card__footer {
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}
</style>
